<template>
  <div
    class="bundle-thumbnail-svg"
    :style="{ padding }"
  >
    <svg
      v-if="frameSpecs"
      class="gallery-wall-thumbnail-svg"
      xmlns="http://www.w3.org/2000/svg"
      width="100%"
      height="100%"
      tabindex="-1"
      :viewBox="viewBox"
    >
      <template v-for="(frameSpec, frameKey) in frameSpecs">
        <RenderSvgFrame
          :id="`gallery-${id}-frame-${frameKey}`"
          :frameSpec="frameSpec"
          :x="frameSpec.offsetInBundle.leftInInches"
          :y="frameSpec.offsetInBundle.topInInches"
          :inBundle="{ frameKey }"
          :readonly="true"
          nestedSVG
        />
      </template>
    </svg>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import axios from 'axios';
import FrameSpec from '@framebridge/toolbox/FrameSpec';
import RenderSvgFrame from '@framebridge/toolbox/components/RenderSvg/RenderSvgFrame.vue';

const imageTypes = [
  'croppedAndNormalized',
  'customerSpecified',
  'normalized',
  'original',
  'originalUnedited',
  'normalizedUnedited'
];
const prependHost = (url, host) => {
  return url.startsWith('http') ? url : `${host}${url}`;
};
const prependHostToArtworkUrl = (artwork, host) => {
  return {
    dimensionsInPixels: artwork.dimensionsInPixels,
    url: prependHost(artwork.url, host)
  };
};
const prependHostToFrameSpecUrls = (frameSpec, host) => {
  return {
    ...frameSpec,
    artworks: frameSpec.artworks.map((artworkSpec) => ({
      ...artworkSpec,
      ...imageTypes.reduce((acc, imageType) => {
        const artwork = artworkSpec[imageType];
        return artwork
          ? {
            ...acc,
            [imageType]: prependHostToArtworkUrl(artwork, host)
          } : acc;
      }, {})
    })),
    moulding: {
      ...frameSpec.moulding,
      url: prependHost(frameSpec.moulding.url, host)
    }
  };
};
const loadBundleSpec = async (bundleSpecNumber, joineryHost, authorizationToken) => {
  const authHeaders = authorizationToken ? { "Framebridge-Resource-Authorization-Token": authorizationToken } : {};
  const res = await axios.get(`${joineryHost}/api/v1/bundle_specifications/${bundleSpecNumber}`, { headers: authHeaders });
  return {
    ...res.data,
    authorizationToken: authorizationToken,
    components: res.data.components.map((component) => ({
      ...component,
      frameSpec: prependHostToFrameSpecUrls(component.frameSpec, joineryHost)
    }))
  };
};

const frameSpecsFromBundle = (bundleSpecData) => {
  return bundleSpecData.components.map((frame) => {
    return {
      ...frame.frameSpec,
      offsetInBundle: {
        leftInInches: frame.fromLeft,
        topInInches: frame.fromTop
      }
    };
  });
};
const initFrameSpecs = async (frameSpecs, joineryHost) => {
  return await Promise.all(frameSpecs.map(async (frameSpec) => {
    const fs = await FrameSpec.build({
      frameSpecData: frameSpec,
      number: frameSpec.id,
      joineryHost
    });

    return fs;
  }));
};

export default defineComponent({
  components: {
    RenderSvgFrame
  },
  props: {
    id: {
      type: String,
      required: true
    },
    bundleSpecNumber: {
      type: String,
      required: false
    },
    token: {
      type: String,
      required: false
    },
    bundleSpec: {
      type: Object,
      required: false
    },
    previewImage: {
      type: String,
      required: false
    },
    padding: {
      type: String,
      default: '10%'
    },
    joineryHost: {
      type: String,
      required: false
    }
  },
  data () {
    return {
      frameSpecs: null,
      bundleWidth: 0,
      bundleHeight: 0,
      xStart: 0
    };
  },
  computed: {
    viewBox () {
      return `${this.xStart}, 0, ${this.bundleWidth}, ${this.bundleHeight}`;
    }
  },
  async mounted () {
    const joineryHost = this.joineryHost || import.meta.env.joineryHost;
    const bundleSpecData = this.bundleSpec
      ? this.bundleSpec
      : this.bundleSpecNumber && this.token
        ? await loadBundleSpec(
          this.bundleSpecNumber,
          joineryHost,
          this.token
        )
        : null;

    if (this.previewImage) {
      bundleSpecData.components[0].frameSpec.artworks[0].croppedAndNormalized.url = this.previewImage;
    }

    if (bundleSpecData) {
      const minimalistFrameSpecs = frameSpecsFromBundle({
        ...bundleSpecData,
        components: bundleSpecData.components.map((component) => ({
          ...component,
          frameSpec: prependHostToFrameSpecUrls(
            component.frameSpec,
            joineryHost
          )
        }))
      });
      this.frameSpecs = await initFrameSpecs(
        minimalistFrameSpecs,
        joineryHost
      );

      const leftOffsets = bundleSpecData.components.map(x => x.fromLeft);
      const minLeft = Math.min(...leftOffsets);
      this.xStart = minLeft;

      const rightEdges = bundleSpecData.components.map(
        x => x.fromLeft + x.frameSpec?.moulding?.exterior?.widthInInches
      );

      this.bundleWidth = Math.max(...rightEdges) - minLeft;
      
      this.bundleHeight = Math.max(
        ...bundleSpecData.components.map((x) => x.fromTop + x.frameSpec?.moulding?.exterior?.heightInInches)
      );
    }
  }
});
</script>

<style lang="scss" scoped>
.bundle-thumbnail-svg {
  align-items: center;
  box-sizing: borderbox;
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;

  .gallery-wall-thumbnail-svg {
    outline: none;
  }
}
</style>
